import {
  PlaylistAddCheck as PlaylistAddCheckIcon,
  Speed as SpeedIcon,
  Timer as TimerIcon,
  Troubleshoot as TroubleshootIcon,
} from "@mui/icons-material";
import { Box, Card, CardContent, Grid, Skeleton, Stack, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { BarChart, LineChart } from "@mui/x-charts";
import { theme } from "../../../theme/theme";
import type { OverviewMetrics } from "../../../types";
import { formatDuration } from "../../../utils";

interface OverviewTabProps {
  data?: OverviewMetrics;
  isLoading?: boolean;
}

function MetricCard({
  title,
  value,
  subtitle,
  icon,
  color,
  valueColor,
  isLoading,
}: {
  title: string;
  value: string | number;
  subtitle: string;
  icon?: React.ReactNode;
  color: string;
  valueColor?: string;
  isLoading?: boolean;
}) {
  return (
    <Card
      sx={{
        height: 160,
        background: alpha(color, 0.03),
      }}
    >
      <CardContent>
        <Stack
          direction="column"
          spacing={0.5}
          alignItems="flex-start"
          justifyContent="space-between"
          sx={{ height: "100%" }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            {icon}
            <Typography variant="subtitle1">{title}</Typography>
          </Stack>
          {isLoading ? (
            <Skeleton variant="text" width={80} height={48} />
          ) : (
            <Typography
              variant="h3"
              component="div"
              sx={{ letterSpacing: -0.5, color: valueColor }}
            >
              {value}
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        </Stack>
      </CardContent>
    </Card>
  );
}

export function OverviewTab({ data, isLoading }: OverviewTabProps) {
  const testRunsByDay = data?.test_runs_by_day ?? {};
  const suiteRunsByDay = data?.suite_runs_by_day ?? {};

  return (
    <Stack spacing={3}>
      {/* KPI Cards */}
      <Grid container spacing={{ xs: 2, md: 3 }}>
        <Grid size={{ xs: 6, md: 3 }}>
          <MetricCard
            title="Total Tests"
            value={data?.total_tests ?? 0}
            subtitle="Test scripts in your environment"
            icon={<TroubleshootIcon color="primary" fontSize="small" />}
            color={theme.palette.primary.main}
            isLoading={isLoading}
          />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <MetricCard
            title="Test Runs"
            value={data?.total_test_runs ?? 0}
            subtitle="Individual test executions in period"
            icon={<SpeedIcon color="info" fontSize="small" />}
            color={theme.palette.info.main}
            isLoading={isLoading}
          />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <MetricCard
            title="Pass Rate"
            value={`${(data?.overall_pass_rate ?? 0).toFixed(1)}%`}
            subtitle="Tests passing successfully"
            color={theme.palette.success.main}
            valueColor={
              (data?.overall_pass_rate ?? 0) > 80
                ? theme.palette.success.main
                : theme.palette.error.main
            }
            isLoading={isLoading}
          />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <MetricCard
            title="Avg Duration"
            value={formatDuration(data?.avg_test_duration_seconds ?? 0)}
            subtitle="Average test execution time"
            icon={<TimerIcon color="warning" fontSize="small" />}
            color={theme.palette.warning.main}
            isLoading={isLoading}
          />
        </Grid>
      </Grid>

      {/* Charts */}
      <Grid container spacing={{ xs: 2, md: 3 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ height: 400 }}>
            <CardContent>
              <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                <PlaylistAddCheckIcon color="primary" fontSize="small" />
                <Typography variant="h6">Test Runs Trend</Typography>
              </Stack>
              <Typography variant="caption" color="text.secondary">
                Daily test execution pass/fail distribution
              </Typography>
              {isLoading ? (
                <Skeleton variant="rectangular" height={300} sx={{ mt: 2 }} />
              ) : Object.keys(testRunsByDay).length > 0 ? (
                <BarChart
                  series={[
                    {
                      color: theme.palette.error.main,
                      data: Object.values(testRunsByDay).map((v) => v.failed),
                      label: "Failed",
                      id: "failedId",
                      stack: "total",
                    },
                    {
                      color: theme.palette.success.main,
                      data: Object.values(testRunsByDay).map((v) => v.passed),
                      label: "Passed",
                      id: "passedId",
                      stack: "total",
                    },
                  ]}
                  xAxis={[
                    {
                      data: Object.keys(testRunsByDay).map((date) =>
                        new Date(date).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                        }),
                      ),
                      scaleType: "band",
                    },
                  ]}
                  height={300}
                />
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: 300,
                  }}
                >
                  <Typography color="text.secondary">No test run data available</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ height: 400 }}>
            <CardContent>
              <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                <PlaylistAddCheckIcon color="secondary" fontSize="small" />
                <Typography variant="h6">Suite Runs Trend</Typography>
              </Stack>
              <Typography variant="caption" color="text.secondary">
                Daily suite execution pass/fail distribution
              </Typography>
              {isLoading ? (
                <Skeleton variant="rectangular" height={300} sx={{ mt: 2 }} />
              ) : Object.keys(suiteRunsByDay).length > 0 ? (
                <LineChart
                  series={[
                    {
                      color: theme.palette.success.main,
                      data: Object.values(suiteRunsByDay).map((v) => v.passed),
                      label: "Passed",
                      curve: "linear",
                    },
                    {
                      color: theme.palette.error.main,
                      data: Object.values(suiteRunsByDay).map((v) => v.failed),
                      label: "Failed",
                      curve: "linear",
                    },
                  ]}
                  xAxis={[
                    {
                      data: Object.keys(suiteRunsByDay).map((date) =>
                        new Date(date).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                        }),
                      ),
                      scaleType: "band",
                    },
                  ]}
                  height={300}
                />
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: 300,
                  }}
                >
                  <Typography color="text.secondary">No suite run data available</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Stack>
  );
}
